
import * as React from 'react';
const { useState } = React;
import { X, Copy, QrCode, Share2, Mail, MessageSquare, ExternalLink, Facebook, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useMobileDetection } from '@/contexts/MobileDetectionContext';
import { useInAppBrowser } from '@/hooks/useInAppBrowser';
import { LAYERS } from '@/utils/layers';

interface ShareOption {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  action: (content: ShareContent) => void | Promise<void>;
}

interface ShareContent {
  title: string;
  content: string;
  url?: string;
  shareText?: string;
}

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  content: ShareContent;
}

// Custom Twitter/X icon component (since there's no dedicated Twitter icon in Lucide)
const TwitterIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <div className={`w-4 h-4 rounded bg-black dark:bg-white flex items-center justify-center text-white dark:text-black text-xs font-bold ${className}`}>
    X
  </div>
);

// WhatsApp icon using MessageCircle with distinct styling
const WhatsAppIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <div className={`w-4 h-4 rounded-full bg-green-500 flex items-center justify-center ${className}`}>
    <MessageCircle size={size * 0.7} className="text-white" fill="white" />
  </div>
);

// Telegram icon using a paper plane style
const TelegramIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <div className={`w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center ${className}`}>
    <div className="w-2 h-2 bg-white" style={{
      clipPath: 'polygon(0% 0%, 100% 50%, 0% 100%, 20% 50%)'
    }}></div>
  </div>
);

// Icon wrapper for Lucide icons to match our interface
const IconWrapper = ({ LucideIcon, ...props }: { LucideIcon: React.ComponentType<any>, size?: number, className?: string }) => (
  <LucideIcon size={props.size || 16} className={props.className || "text-gray-600 dark:text-gray-400"} />
);

export default function ShareDialog({ isOpen, onClose, content }: ShareDialogProps) {
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const { toast } = useToast();
  const { isMobile } = useMobileDetection();

  const generateShareUrl = () => {
    if (content.url) return content.url;
    return window.location.href;
  };

  const generateShareText = () => {
    if (content.shareText) return content.shareText;
    return `${content.title}\n\n${content.content}`;
  };

  const handleCopyLink = async () => {
    try {
      const url = generateShareUrl();
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "The link has been copied to your clipboard.",
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Failed to copy link to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleCopyText = async () => {
    try {
      const text = generateShareText();
      await navigator.clipboard.writeText(text);
      toast({
        title: "Text copied!",
        description: "The content has been copied to your clipboard.",
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Failed to copy text to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: content.title,
          text: content.content,
          url: generateShareUrl(),
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          toast({
            title: "Share failed",
            description: "Failed to share content.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleSocialShare = async (platform: string) => {
    const { openInAppBrowser } = useInAppBrowser();
    const url = generateShareUrl();
    const text = encodeURIComponent(generateShareText());
    const encodedUrl = encodeURIComponent(url);
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${text}%20${encodedUrl}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${text}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(content.title)}&body=${text}%0A%0A${encodedUrl}`;
        break;
      case 'sms':
        shareUrl = `sms:?body=${text}%20${encodedUrl}`;
        break;
    }
    
    if (shareUrl) {
      await openInAppBrowser({ url: shareUrl });
    }
  };

  const handleGenerateQR = async () => {
    setIsGeneratingQR(true);
    try {
      const url = generateShareUrl();
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
      
      // Show QR code inline instead of new window
      toast({
        title: "QR Code URL",
        description: `QR: ${qrUrl}`,
      });
      
    } catch (error) {
      toast({
        title: "QR Code failed",
        description: "Failed to generate QR code.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const shareOptions: ShareOption[] = [
    // Social Media
    {
      id: 'facebook',
      name: 'Facebook',
      icon: (props: any) => <IconWrapper LucideIcon={Facebook} {...props} className="text-blue-600" />,
      color: 'hover:bg-blue-50 dark:hover:bg-blue-950',
      action: () => handleSocialShare('facebook'),
    },
    {
      id: 'twitter',
      name: 'Twitter/X',
      icon: TwitterIcon,
      color: 'hover:bg-gray-50 dark:hover:bg-gray-800',
      action: () => handleSocialShare('twitter'),
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      color: 'hover:bg-green-50 dark:hover:bg-green-950',
      action: () => handleSocialShare('whatsapp'),
    },
    // Messaging
    {
      id: 'telegram',
      name: 'Telegram',
      icon: TelegramIcon,
      color: 'hover:bg-blue-50 dark:hover:bg-blue-950',
      action: () => handleSocialShare('telegram'),
    },
    {
      id: 'email',
      name: 'Email',
      icon: (props: any) => <IconWrapper LucideIcon={Mail} {...props} />,
      color: 'hover:bg-gray-50 dark:hover:bg-gray-800',
      action: () => handleSocialShare('email'),
    },
    {
      id: 'sms',
      name: 'Message',
      icon: (props: any) => <IconWrapper LucideIcon={MessageSquare} {...props} />,
      color: 'hover:bg-gray-50 dark:hover:bg-gray-800',
      action: () => handleSocialShare('sms'),
    },
    // Utility
    {
      id: 'copy-link',
      name: 'Copy Link',
      icon: (props: any) => <IconWrapper LucideIcon={ExternalLink} {...props} />,
      color: 'hover:bg-gold/10 dark:hover:bg-gold/20',
      action: handleCopyLink,
    },
    {
      id: 'copy-text',
      name: 'Copy Text',
      icon: (props: any) => <IconWrapper LucideIcon={Copy} {...props} />,
      color: 'hover:bg-gold/10 dark:hover:bg-gold/20',
      action: handleCopyText,
    },
    {
      id: 'qr-code',
      name: 'QR Code',
      icon: (props: any) => <IconWrapper LucideIcon={QrCode} {...props} />,
      color: 'hover:bg-gold/10 dark:hover:bg-gold/20',
      action: handleGenerateQR,
    },
  ];

  // Add native share option for mobile
  if (isMobile && navigator.share) {
    shareOptions.unshift({
      id: 'native-share',
      name: 'Share',
      icon: (props: any) => <IconWrapper LucideIcon={Share2} {...props} />,
      color: 'hover:bg-gold/10 dark:hover:bg-gold/20',
      action: handleNativeShare,
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-xs bg-background text-foreground border-2 border-gold z-prayer-dialogs"
        style={{ zIndex: LAYERS.SECONDARY_DIALOGS }}
      >
        <DialogHeader>
          <DialogTitle className="app-card-title text-black dark:text-white text-sm">
            Share Prayer
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3">
          {/* Content Preview */}
          <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="app-meta-text font-medium text-black dark:text-white mb-1 text-xs">
              {content.title}
            </h4>
            <p className="app-meta-text text-gray-600 dark:text-gray-400 line-clamp-2 text-xs">
              {content.content}
            </p>
          </div>

          {/* Share Options Grid */}
          <div className={`grid gap-1 ${isMobile ? 'grid-cols-3' : 'grid-cols-4'}`}>
            {shareOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => option.action(content)}
                  disabled={option.id === 'qr-code' && isGeneratingQR}
                  className={`
                    flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-200 dark:border-gray-700
                    transition-all duration-200 ${option.color}
                    hover:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/20
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                  aria-label={`Share via ${option.name}`}
                >
                  <IconComponent size={16} />
                  <span className="app-meta-text text-gray-700 dark:text-gray-300 text-center text-xs">
                    {option.id === 'qr-code' && isGeneratingQR ? 'Loading...' : option.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <Button
          variant="outline"
          onClick={onClose}
          className="w-full mt-3 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm py-2"
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
}
